import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: clientRows, error: clientErr } = await supabase
        .from('agency_clients')
        .select('*')
        .order('name');

      if (clientErr) throw clientErr;

      const { data: postRows, error: postErr } = await supabase
        .from('agency_posts')
        .select('*')
        .order('date');

      if (postErr) throw postErr;

      const postsByClient = {};
      for (const post of postRows) {
        if (!postsByClient[post.client_id]) postsByClient[post.client_id] = [];
        postsByClient[post.client_id].push({
          id: post.id,
          number: post.number,
          clientId: post.client_id,
          weekday: post.weekday,
          day: post.day,
          month: post.month,
          date: post.date,
          week: post.week,
          title: post.title,
          workingTitle: post.working_title,
          format: post.format,
          formatLinkedIn: post.format_linkedin,
          withFace: post.with_face,
          pillar: post.pillar,
          funnelStage: post.funnel_stage,
          objective: post.objective,
          spokesperson: post.spokesperson,
          content: post.content || '',
          status: post.status,
        });
      }

      const mapped = clientRows.map(c => ({
        id: c.id,
        name: c.name,
        segment: c.segment,
        positioning: c.positioning,
        channels: c.channels || [],
        cadence: c.cadence,
        brandGuide: c.brand_guide,
        posts: postsByClient[c.id] || [],
        calendars: postsByClient[c.id]?.length ? [{ name: 'supabase', postCount: postsByClient[c.id].length }] : [],
      }));

      setClients(mapped);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const getClient = useCallback(
    (id) => clients.find(c => c.id === id) || null,
    [clients]
  );

  const createClient = useCallback(async (clientData) => {
    const id = clientData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const { error: err } = await supabase
      .from('clients')
      .insert({
        id,
        name: clientData.name,
        segment: clientData.segment || '',
        positioning: clientData.positioning || '',
        channels: clientData.channels || [],
        cadence: clientData.cadence || '',
        brand_guide: clientData.brandGuide || '',
      });
    if (err) throw err;
    await fetchClients();
    return id;
  }, [fetchClients]);

  const updateClient = useCallback(async (id, clientData) => {
    const { error: err } = await supabase
      .from('clients')
      .update({
        name: clientData.name,
        segment: clientData.segment || '',
        positioning: clientData.positioning || '',
        channels: clientData.channels || [],
        cadence: clientData.cadence || '',
        brand_guide: clientData.brandGuide || '',
      })
      .eq('id', id);
    if (err) throw err;
    await fetchClients();
  }, [fetchClients]);

  const deleteClient = useCallback(async (id) => {
    const { error: err } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);
    if (err) throw err;
    await fetchClients();
  }, [fetchClients]);

  return { clients, loading, error, getClient, createClient, updateClient, deleteClient, refetch: fetchClients };
}
